import { useState, type ReactNode } from 'react'
import {
  createFileRoute,
  Link,
  useNavigate,
  useSearch,
} from '@tanstack/react-router'
import { toast } from 'sonner'
import { z } from 'zod'
import { MoreVertical } from 'lucide-react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ComboboxField } from '@/components/ui/combobox'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { EditableField } from '@/components/ui/editable-field'
import { EmailLink } from '@/components/ui/email-link'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ContactPhones } from '@/components/contacts/contact-phones'
import {
  useContact,
  useUpdateContact,
} from '@/hooks/use-contacts'
import { useAccounts } from '@/hooks/use-accounts'
import { useAccountDivisions } from '@/hooks/use-account-divisions'
import { relativeTime } from '@/lib/utils'
import type { Database } from '@/types/database'

type ContactUpdate = Database['public']['Tables']['contacts']['Update']

const CONTACT_TYPE_LABELS: Record<string, string> = {
  reseller_rep: 'Reseller Rep',
  end_user_contact: 'End User Contact',
  distributor_rep: 'Distributor Rep',
}

const CONTACT_TYPE_COLORS: Record<string, string> = {
  reseller_rep: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  end_user_contact:
    'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
  distributor_rep:
    'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
}

const detailSearch = z.object({
  tab: z.enum(['overview', 'activity']).optional().default('overview'),
})

export const Route = createFileRoute('/_authed/contacts/$contactId')({
  validateSearch: detailSearch,
  component: ContactDetailPage,
})

function ContactDetailPage() {
  const { contactId } = Route.useParams()
  const { tab } = useSearch({ from: '/_authed/contacts/$contactId' })
  const navigate = useNavigate({ from: Route.fullPath })
  const { data: contact, isLoading, error } = useContact(contactId)
  const update = useUpdateContact(contactId)

  const [markInactiveOpen, setMarkInactiveOpen] = useState(false)

  if (isLoading) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-5 w-48" />
        <Skeleton className="h-48 w-full" />
      </div>
    )
  }

  if (error || !contact) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertDescription>
            Contact not found.{' '}
            <Link to="/contacts" className="underline">
              Back to contacts
            </Link>
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  type ContactWithJoins = typeof contact & {
    account?: { id: string; name: string } | null
    division?: { id: string; name: string } | null
  }
  const c = contact as ContactWithJoins

  const fullName = `${contact.first_name} ${contact.last_name}`

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="border-b bg-background px-6 py-4">
        <div className="mb-2 flex items-center gap-1 text-sm text-muted-foreground">
          {c.account && (
            <>
              <Link
                to="/accounts/$accountId"
                params={{ accountId: c.account.id }}
                className="hover:underline"
              >
                {c.account.name}
              </Link>
              {c.division && (
                <>
                  <span>/</span>
                  <span>{c.division.name}</span>
                </>
              )}
              <span>/</span>
            </>
          )}
          <span>{fullName}</span>
        </div>
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-bold">{fullName}</h2>
          <Badge
            variant="secondary"
            className={`font-normal ${CONTACT_TYPE_COLORS[contact.contact_type] ?? ''}`}
          >
            {CONTACT_TYPE_LABELS[contact.contact_type] ?? contact.contact_type}
          </Badge>
          {!contact.is_active && (
            <Badge
              variant="outline"
              className="font-normal text-muted-foreground"
            >
              Inactive
            </Badge>
          )}
          <div className="ml-auto">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreVertical size={16} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {contact.is_active ? (
                  <DropdownMenuItem onClick={() => setMarkInactiveOpen(true)}>
                    Mark inactive
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem
                    onClick={() => {
                      void update
                        .mutateAsync({ is_active: true })
                        .then(() => toast.success('Contact reactivated'))
                    }}
                  >
                    Reactivate
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        {contact.title && (
          <p className="mt-1 text-sm text-muted-foreground">{contact.title}</p>
        )}
      </div>

      <Tabs
        value={tab}
        onValueChange={(v) =>
          void navigate({
            to: '/contacts/$contactId',
            params: { contactId },
            search: () => ({ tab: v as typeof tab }),
          })
        }
        className="flex flex-1 flex-col overflow-hidden"
      >
        <div className="shrink-0 border-b">
          <TabsList className="h-10 w-full justify-start gap-0 rounded-none bg-transparent p-0 px-6">
            {(['overview', 'activity'] as const).map((t) => (
              <TabsTrigger
                key={t}
                value={t}
                className="h-10 rounded-none border-b-2 border-transparent px-4 capitalize data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none"
              >
                {t}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        <div className="flex-1 overflow-auto">
          <TabsContent value="overview" className="mt-0 p-6">
            <OverviewContent
              contactId={contactId}
              contact={contact}
              isActive={contact.is_active}
            />
          </TabsContent>
          <TabsContent value="activity" className="mt-0 p-6">
            <p className="text-sm text-muted-foreground">
              Activity log coming in Phase 1d.
            </p>
          </TabsContent>
        </div>
      </Tabs>

      {/* Mark inactive confirm */}
      <AlertDialog open={markInactiveOpen} onOpenChange={setMarkInactiveOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark {fullName} as inactive?</AlertDialogTitle>
            <AlertDialogDescription>
              The contact will be hidden from the default contact list. You can
              reactivate them at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                void update
                  .mutateAsync({ is_active: false })
                  .then(() => toast.success('Contact marked inactive'))
              }}
            >
              Mark inactive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

function FieldRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-start gap-3">
      <span className="w-32 shrink-0 pt-1 text-sm text-muted-foreground">
        {label}
      </span>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  )
}

function OverviewContent({
  contactId,
  contact,
  isActive,
}: {
  contactId: string
  contact: NonNullable<ReturnType<typeof useContact>['data']>
  isActive: boolean
}) {
  const update = useUpdateContact(contactId)
  const { data: accounts = [] } = useAccounts()
  const [pendingAccountId, setPendingAccountId] = useState<string | null>(null)
  const [accountConfirmOpen, setAccountConfirmOpen] = useState(false)

  const watchAccountId = contact.account_id
  const { data: divisions = [] } = useAccountDivisions(watchAccountId ?? '')

  const save = async (field: keyof ContactUpdate, value: unknown) => {
    await update.mutateAsync({ [field]: value } as ContactUpdate)
    toast.success('Saved')
  }

  const accountOptions = accounts.map((a) => ({ value: a.id, label: a.name }))
  const divisionOptions = divisions.map((d) => ({ value: d.id, label: d.name }))

  const currentAccountName =
    accounts.find((a) => a.id === contact.account_id)?.name ?? '—'
  const pendingAccountName =
    accounts.find((a) => a.id === pendingAccountId)?.name ?? '—'

  return (
    <div className="max-w-lg space-y-4">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Details
      </h3>

      <FieldRow label="First name">
        <EditableField<string>
          value={contact.first_name}
          onSave={async (v) => save('first_name', v)}
          display={(v) => (
            <span className="text-sm font-medium">{v || '—'}</span>
          )}
          edit={(v, set) => (
            <Input value={v} onChange={(e) => set(e.target.value)} />
          )}
          validate={(v) => (!v.trim() ? 'First name is required' : undefined)}
        />
      </FieldRow>

      <FieldRow label="Last name">
        <EditableField<string>
          value={contact.last_name}
          onSave={async (v) => save('last_name', v)}
          display={(v) => (
            <span className="text-sm font-medium">{v || '—'}</span>
          )}
          edit={(v, set) => (
            <Input value={v} onChange={(e) => set(e.target.value)} />
          )}
          validate={(v) => (!v.trim() ? 'Last name is required' : undefined)}
        />
      </FieldRow>

      <FieldRow label="Title">
        <EditableField<string>
          value={contact.title ?? ''}
          onSave={async (v) => save('title', v || null)}
          display={(v) => <span className="text-sm">{v || '—'}</span>}
          edit={(v, set) => (
            <Input
              value={v}
              onChange={(e) => set(e.target.value)}
              placeholder="e.g. Account Manager"
            />
          )}
        />
      </FieldRow>

      <FieldRow label="Type">
        <EditableField<string>
          value={contact.contact_type}
          onSave={async (v) => save('contact_type', v)}
          display={(v) => (
            <Badge
              variant="secondary"
              className={`font-normal ${CONTACT_TYPE_COLORS[v] ?? ''}`}
            >
              {CONTACT_TYPE_LABELS[v] ?? v}
            </Badge>
          )}
          edit={(v, set) => (
            <Select value={v} onValueChange={set}>
              <SelectTrigger className="h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="reseller_rep">Reseller Rep</SelectItem>
                <SelectItem value="end_user_contact">
                  End User Contact
                </SelectItem>
                <SelectItem value="distributor_rep">Distributor Rep</SelectItem>
              </SelectContent>
            </Select>
          )}
        />
      </FieldRow>

      <FieldRow label="Email">
        <EditableField<string>
          value={contact.email ?? ''}
          onSave={async (v) => save('email', v || null)}
          display={(v) => <EmailLink email={v} />}
          edit={(v, set) => (
            <Input
              value={v}
              onChange={(e) => set(e.target.value)}
              type="email"
            />
          )}
        />
      </FieldRow>

      <FieldRow label="Account">
        <ComboboxField
          options={accountOptions}
          value={contact.account_id || undefined}
          onSave={async () => {
            // no-op: actual save is performed by the confirm dialog below
          }}
          onBeforeSave={async (next, current) => {
            if (next === current) return true
            setPendingAccountId(next ?? null)
            setAccountConfirmOpen(true)
            return false
          }}
          display={(v) => {
            const a = accounts.find((acc) => acc.id === v)
            return a ? (
              <Link
                to="/accounts/$accountId"
                params={{ accountId: a.id }}
                className="text-sm text-primary hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                {a.name}
              </Link>
            ) : (
              <span className="text-sm text-muted-foreground">—</span>
            )
          }}
        />
      </FieldRow>

      <FieldRow label="Division">
        <ComboboxField
          options={divisionOptions}
          value={contact.division_id || undefined}
          onSave={async (v) => save('division_id', v ?? null)}
          display={(v) => {
            const d = divisions.find((div) => div.id === v)
            return d ? (
              <span className="text-sm">{d.name}</span>
            ) : (
              <span className="text-sm text-muted-foreground">—</span>
            )
          }}
          allowClear
          disabled={!watchAccountId}
        />
      </FieldRow>

      {/* Phones section */}
      <div className="pt-1">
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Phones
        </h3>
        <ContactPhones contactId={contactId} mode="edit" disabled={!isActive} />
      </div>

      <div className="flex gap-6 border-t pt-3 text-xs text-muted-foreground">
        <span>Created {new Date(contact.created_at).toLocaleDateString()}</span>
        <span>Updated {relativeTime(contact.updated_at)}</span>
      </div>

      {/* Account change confirm dialog */}
      <AlertDialog
        open={accountConfirmOpen}
        onOpenChange={setAccountConfirmOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Move this contact?</AlertDialogTitle>
            <AlertDialogDescription>
              Move {contact.first_name} {contact.last_name} from{' '}
              <strong>{currentAccountName}</strong> to{' '}
              <strong>{pendingAccountName}</strong>? Their division will be
              cleared and you'll need to reassign one if applicable. Activity
              history stays attached to this contact.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingAccountId(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (!pendingAccountId) return
                await update.mutateAsync({
                  account_id: pendingAccountId,
                  division_id: null,
                })
                toast.success('Account updated')
                setPendingAccountId(null)
              }}
            >
              Move contact
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
